import React, { useState, useEffect, useMemo } from 'react';
import { Switch, Select, Form, message, Spin, Row, Col, Card, Tag, Space, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import { StandardCard } from '../common';
import { theme } from '../../theme';
import { InfoCircleOutlined, TableOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface DatosFijos {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  sigla: string;
  valor_string: string;
  valor_numerico: string;
  alta: number;
}

const PlayaModulosRollos: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const [hastaFila, setHastaFila] = useState<string>('B');
  const [hastaColumna, setHastaColumna] = useState<string>('2');
  const [datosFijos, setDatosFijos] = useState<DatosFijos | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(false);
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  // Generar letras de B a Z
  const letras = Array.from({ length: 25 }, (_, i) => 
    String.fromCharCode(66 + i)
  );

  // Generar números del 2 al 25
  const numeros = Array.from({ length: 24 }, (_, i) => 
    (i + 2).toString()
  );

  const fetchDatosFijos = async () => {
    if (!id_empresa || !id_sucursal) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await apiClient.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PMR`,
        { token }
      );

      const isEmpty = !response.data || response.data.length === 0;
      
      if (response.data[0]?.valor_string) {
        const [filasRango, columnasRango] = response.data[0].valor_string.split('=');
        const [_desdeFila, hastaFilaValue] = filasRango.split('-');
        const [_desdeCol, hastaColumnaValue] = columnasRango.split('-');
        
        if (hastaFilaValue) setHastaFila(hastaFilaValue);
        if (hastaColumnaValue) setHastaColumna(hastaColumnaValue);
      }
      const activarEnabled = isEmpty || (!isEmpty && response.data[0].alta === 0);
      setIsActive(!activarEnabled);
      setDatosFijos(response.data[0]);

    } catch (error) {
      console.error('Error al obtener datos:', error);
      messageApi.error('Error al cargar los datos iniciales');
      setIsActive(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatosFijos();
  }, [id_empresa, id_sucursal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchChange = async (checked: boolean) => {
    if (!id_empresa || !id_sucursal) return;

    // Actualizar estado optimísticamente
    const previousState = isActive;
    setIsActive(checked);

    try {
      const token = await getAccessTokenSilently();
      
      if (checked) {
        // Activar
        // Construir el valorstring con el formato A-{hastaFila}/1-{hastaColumna}
        const valorstring = `A-${hastaFila}=1-${hastaColumna}`;
        
        // Llamada a la API para grabar datos
        const response = await apiClient.post<DatosFijos>(
          `${process.env.REACT_APP_RELACION_URL}/grabar_datos_fijos/${id_empresa}/${id_sucursal}/PMR/${valorstring}/0`,
          {},
          { token }
        );
        messageApi.success('Playa activada correctamente');
        // Actualizar datosFijos con la respuesta si está disponible
        if (response.data) {
          setDatosFijos(response.data);
        }
      } else {
        // Desactivar
        await apiClient.post(
          `${process.env.REACT_APP_RELACION_URL}/desactivar_datos_fijos/${id_empresa}/${id_sucursal}/PMR`,
          {},
          { token }
        );
        messageApi.success('Playa desactivada correctamente');
        setDatosFijos(null);
      }
      
      // No recargar todo el componente, solo mantener el estado actualizado

    } catch (error) {
      console.error('Error:', error);
      messageApi.error(checked ? 'Error al activar la playa' : 'Error al desactivar la playa');
      // Revertir el estado en caso de error
      setIsActive(previousState);
    }
  };

  // Calcular el rango total de celdas
  const rangoTotal = useMemo(() => {
    const filaInicio = 'A';
    const filaFin = hastaFila;
    const colInicio = 1;
    const colFin = parseInt(hastaColumna);
    
    const filas = filaFin.charCodeAt(0) - filaInicio.charCodeAt(0) + 1;
    const columnas = colFin - colInicio + 1;
    const totalCeldas = filas * columnas;
    
    return { filas, columnas, totalCeldas };
  }, [hastaFila, hastaColumna]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      
      {/* Información descriptiva compacta */}
      <StandardCard
        style={{ marginBottom: '24px' }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <InfoCircleOutlined style={{ color: theme.colors.primary, fontSize: '18px' }} />
            <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary }}>
              Playa Módulos/Rollos
            </Text>
          </div>
          <Text style={{ color: theme.colors.textSecondary, fontSize: '14px', display: 'block' }}>
            Sector de depósitos de módulos y rollos en una superficie asignada. 
            Configure el rango de la cuadrícula (Filas A-Z, Columnas 1-25) y active el espacio.
          </Text>
        </Space>
      </StandardCard>

      {/* Configuración principal en grid */}
      <Row gutter={[24, 24]} style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Configuración de Rangos */}
        <Col xs={24} lg={16} style={{ display: 'flex' }}>
          <StandardCard
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TableOutlined />
                Configuración de Rango
              </span>
            }
            style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <Row gutter={[24, 24]}>
              {/* Filas */}
              <Col xs={24} sm={12}>
                <Card 
                  size="small"
                  style={{
                    backgroundColor: theme.colors.backgroundGray,
                    border: `1px solid ${theme.colors.borderLight}`,
                    borderRadius: theme.styles.borderRadius,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%', flex: 1, justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: '14px', color: theme.colors.primaryDark }}>
                      FILAS
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '100px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Desde
                        </Text>
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: `1px solid ${theme.colors.borderLight}`,
                          textAlign: 'center',
                          fontWeight: 600,
                          color: theme.colors.textSecondary
                        }}>
                          A
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', color: theme.colors.textSecondary }}>→</div>
                      <div style={{ flex: 1, minWidth: '100px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Hasta
                        </Text>
                        <Select
                          value={hastaFila}
                          onChange={(value) => setHastaFila(value)}
                          style={{ width: '100%' }}
                          size="large"
                        >
                          {letras.map(letra => (
                            <Select.Option key={letra} value={letra}>
                              {letra}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div style={{
                      padding: '8px',
                      backgroundColor: '#fff',
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: `1px solid ${theme.colors.borderLight}`
                    }}>
                      <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                        Rango: <Text strong style={{ color: theme.colors.primary }}>A-{hastaFila}</Text>
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>

              {/* Columnas */}
              <Col xs={24} sm={12} style={{ display: 'flex' }}>
                <Card 
                  size="small"
                  style={{
                    backgroundColor: theme.colors.backgroundGray,
                    border: `1px solid ${theme.colors.borderLight}`,
                    borderRadius: theme.styles.borderRadius,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: '14px', color: theme.colors.primaryDark }}>
                      COLUMNAS
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '100px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Desde
                        </Text>
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: `1px solid ${theme.colors.borderLight}`,
                          textAlign: 'center',
                          fontWeight: 600,
                          color: theme.colors.textSecondary
                        }}>
                          1
                        </div>
                      </div>
                      <div style={{ fontSize: '20px', color: theme.colors.textSecondary }}>→</div>
                      <div style={{ flex: 1, minWidth: '100px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Hasta
                        </Text>
                        <Select
                          value={hastaColumna}
                          onChange={(value) => setHastaColumna(value)}
                          style={{ width: '100%' }}
                          size="large"
                        >
                          {numeros.map(numero => (
                            <Select.Option key={numero} value={numero}>
                              {numero}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div style={{
                      padding: '8px',
                      backgroundColor: '#fff',
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: `1px solid ${theme.colors.borderLight}`
                    }}>
                      <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                        Rango: <Text strong style={{ color: theme.colors.primary }}>1-{hastaColumna}</Text>
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </StandardCard>
        </Col>

        {/* Resumen y Estado */}
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <StandardCard 
            title="Resumen"
            style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%', flex: 1, justifyContent: 'space-between' }}>
              {/* Vista previa del rango */}
              <div style={{
                padding: '16px',
                backgroundColor: theme.colors.backgroundGray,
                borderRadius: theme.styles.borderRadius,
                border: `1px solid ${theme.colors.borderLight}`
              }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  Rango Total Configurado
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '14px' }}>Filas:</Text>
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      A-{hastaFila} ({rangoTotal.filas})
                    </Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '14px' }}>Columnas:</Text>
                    <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                      1-{hastaColumna} ({rangoTotal.columnas})
                    </Tag>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '12px',
                    borderTop: `1px solid ${theme.colors.borderLight}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Text strong style={{ fontSize: '14px' }}>Total Celdas:</Text>
                    <Tag color="purple" style={{ fontSize: '16px', fontWeight: 600, padding: '6px 16px' }}>
                      {rangoTotal.totalCeldas.toLocaleString('es-AR')}
                    </Tag>
                  </div>
                </div>
              </div>

              {/* Estado */}
              <div style={{
                padding: '16px',
                backgroundColor: isActive ? '#f6ffed' : '#fff7e6',
                borderRadius: theme.styles.borderRadius,
                border: `2px solid ${isActive ? '#b7eb8f' : '#ffe58f'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '14px', color: theme.colors.textPrimary }}>
                    Estado:
                  </Text>
                  <Tag 
                    color={isActive ? 'success' : 'warning'} 
                    style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 600 }}
                  >
                    {isActive ? 'Activa' : 'Inactiva'}
                  </Tag>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: '12px'
                }}>
                  <Text style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                    {isActive ? 'Playa activa' : 'Playa inactiva'}
                  </Text>
                  <Switch
                    checked={isActive}
                    onChange={handleSwitchChange}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    size="small"
                  />
                </div>
              </div>
            </Space>
          </StandardCard>
        </Col>
      </Row>
    </>
  );
};

export default PlayaModulosRollos;
